import {
    createContext,
    useCallback,
    useContext,
    useEffect,
    useMemo,
    useRef,
    useState,
} from "react";
import { api, TOKEN_KEY } from "./api";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
    const [user, setUser] = useState(null);
    const [ready, setReady] = useState(false);
    // Login modal state (opened by requireAuth callbacks)
    const [modalOpen, setModalOpen] = useState(false);
    const pendingActionRef = useRef(null);

    const refresh = useCallback(async () => {
        const t = localStorage.getItem(TOKEN_KEY);
        if (!t) {
            setUser(null);
            setReady(true);
            return null;
        }
        try {
            const { data } = await api.get("/auth/me");
            setUser(data);
            return data;
        } catch {
            localStorage.removeItem(TOKEN_KEY);
            setUser(null);
            return null;
        } finally {
            setReady(true);
        }
    }, []);

    useEffect(() => {
        refresh();
    }, [refresh]);

    // Google Sign-In redirect handler (parse #session_id=... from URL hash on load)
    useEffect(() => {
        const hash = window.location.hash || "";
        const m = hash.match(/session_id=([^&]+)/);
        if (!m) return;
        const sessionId = decodeURIComponent(m[1]);
        (async () => {
            try {
                const { data } = await api.post("/auth/google-session", {
                    session_id: sessionId,
                });
                localStorage.setItem(TOKEN_KEY, data.token);
                setUser(data.user);
                window.history.replaceState(
                    null,
                    "",
                    window.location.pathname + window.location.search,
                );
            } catch (e) {
                console.error("google-session failed", e);
            }
        })();
    }, []);

    const setToken = useCallback(async (token) => {
        localStorage.setItem(TOKEN_KEY, token);
        await refresh();
    }, [refresh]);

    const login = useCallback(
        async (email, password) => {
            const { data } = await api.post("/auth/login", { email, password });
            localStorage.setItem(TOKEN_KEY, data.token);
            setUser(data.user);
            return data.user;
        },
        [],
    );

    const register = useCallback(
        async (payload) => {
            const { data } = await api.post("/auth/register", payload);
            localStorage.setItem(TOKEN_KEY, data.token);
            setUser(data.user);
            return data.user;
        },
        [],
    );

    const registerDoctor = useCallback(
        async (payload) => {
            const { data } = await api.post("/auth/register-doctor", payload);
            localStorage.setItem(TOKEN_KEY, data.token);
            setUser(data.user);
            return data.user;
        },
        [],
    );

    const logout = useCallback(async () => {
        try {
            await api.post("/auth/logout");
        } catch (err) {
            // Best-effort — a failed logout still clears the client-side session below.
            // eslint-disable-next-line no-console
            console.warn("Logout API call failed:", err?.response?.status || err?.message || err);
        }
        localStorage.removeItem(TOKEN_KEY);
        setUser(null);
    }, []);

    // requireAuth(fn) — if logged in, fires fn immediately.
    // Otherwise opens login modal and defers fn until successful login.
    const requireAuth = useCallback((fn) => {
        if (user) {
            fn?.();
            return;
        }
        pendingActionRef.current = fn || null;
        setModalOpen(true);
    }, [user]);

    const closeModal = useCallback(() => {
        setModalOpen(false);
        pendingActionRef.current = null;
    }, []);

    const onAuthenticated = useCallback(() => {
        setModalOpen(false);
        const cb = pendingActionRef.current;
        pendingActionRef.current = null;
        // Fire the deferred action after modal closes
        if (cb) setTimeout(cb, 30);
    }, []);

    const value = useMemo(
        () => ({
            user,
            ready,
            login,
            register,
            registerDoctor,
            logout,
            setToken,
            refresh,
            requireAuth,
            modalOpen,
            closeModal,
            onAuthenticated,
        }),
        [
            user,
            ready,
            login,
            register,
            registerDoctor,
            logout,
            setToken,
            refresh,
            requireAuth,
            modalOpen,
            closeModal,
            onAuthenticated,
        ],
    );

    return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
    const ctx = useContext(AuthContext);
    if (!ctx) throw new Error("useAuth must be used inside AuthProvider");
    return ctx;
}
